/**
 * Rax Landing Page
 *
 * Modern hero page for unauthenticated users.
 * "Your AI Band" — click to get started.
 */

import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { raxTheme } from "../styles/rax-theme.js";

@customElement("rax-landing")
export class RaxLanding extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
        background: var(--bg-primary);
        min-height: 100vh;
        overflow-x: hidden;
      }
      
      /* Hero Section */
      .hero {
        min-height: 100vh;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: var(--space-6);
        text-align: center;
        position: relative;
        overflow: hidden;
      }
      
      /* Animated background */
      .hero::before {
        content: "";
        position: absolute;
        top: -50%;
        left: -50%;
        width: 200%;
        height: 200%;
        background:
          radial-gradient(circle at 30% 30%, rgba(255, 208, 0, 0.08) 0%, transparent 50%),
          radial-gradient(circle at 70% 70%, rgba(255, 208, 0, 0.05) 0%, transparent 40%);
        animation: drift 20s ease-in-out infinite;
        z-index: 0;
      }
      
      @keyframes drift {
        0%,
        100% {
          transform: translate(0, 0) rotate(0deg);
        }
        50% {
          transform: translate(-5%, -5%) rotate(3deg);
        }
      }
      
      .hero-content {
        position: relative;
        z-index: 1;
        max-width: 800px;
      }
      
      .logo {
        font-size: 100px;
        margin-bottom: var(--space-4);
        display: block;
        filter: drop-shadow(0 0 40px var(--accent-glow));
        animation: pulse 3s ease-in-out infinite;
      }
      
      @keyframes pulse {
        0%,
        100% {
          transform: scale(1);
          filter: drop-shadow(0 0 40px var(--accent-glow));
        }
        50% {
          transform: scale(1.05);
          filter: drop-shadow(0 0 60px var(--accent-glow));
        }
      }
      
      .title {
        font-size: clamp(48px, 10vw, 80px);
        font-weight: 800;
        color: var(--text-primary);
        margin-bottom: var(--space-4);
        letter-spacing: -0.02em;
        line-height: 1.1;
      }
      
      .title .accent {
        color: var(--rax-yellow);
        text-shadow: 0 0 60px var(--accent-glow);
      }
      
      .subtitle {
        font-size: clamp(18px, 3vw, 24px);
        color: var(--text-secondary);
        margin-bottom: var(--space-8);
        max-width: 600px;
        margin-left: auto;
        margin-right: auto;
        line-height: 1.6;
      }
      
      /* CTA Section */
      .cta-section {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--space-4);
      }
      
      .cta-button {
        display: inline-flex;
        align-items: center;
        gap: var(--space-3);
        padding: var(--space-4) var(--space-8);
        background: var(--rax-yellow);
        color: var(--rax-black);
        border: none;
        border-radius: var(--radius-xl);
        font-size: var(--font-xl);
        font-weight: 700;
        cursor: pointer;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 0 4px 20px rgba(255, 208, 0, 0.3);
      }
      
      .cta-button:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 40px rgba(255, 208, 0, 0.5);
        background: var(--rax-gold);
      }
      
      .cta-button:active {
        transform: translateY(0);
      }
      
      .cta-button .icon {
        font-size: 24px;
      }
      
      .cta-secondary {
        display: inline-flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-3) var(--space-5);
        background: transparent;
        color: var(--text-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
        font-size: var(--font-md);
        font-weight: 500;
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .cta-secondary:hover {
        color: var(--text-primary);
        border-color: var(--rax-yellow);
      }
      
      /* Features Grid */
      .features {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        gap: var(--space-6);
        margin-top: var(--space-12);
        text-align: left;
      }
      
      .feature {
        padding: var(--space-5);
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
        transition: all var(--transition-fast);
      }
      
      .feature:hover {
        border-color: var(--rax-yellow);
        transform: translateY(-4px);
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
      }
      
      .feature-icon {
        font-size: 32px;
        margin-bottom: var(--space-3);
      }
      
      .feature-title {
        font-size: var(--font-lg);
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: var(--space-2);
      }
      
      .feature-desc {
        font-size: var(--font-sm);
        color: var(--text-secondary);
        line-height: 1.6;
      }
      
      /* Footer */
      .hero-footer {
        position: absolute;
        bottom: var(--space-6);
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        gap: var(--space-6);
        color: var(--text-tertiary);
        font-size: var(--font-sm);
      }
      
      .hero-footer a {
        color: var(--text-tertiary);
        text-decoration: none;
        transition: color var(--transition-fast);
      }
      
      .hero-footer a:hover {
        color: var(--rax-yellow);
      }
      
      /* How It Works Section */
      .how-it-works {
        margin-top: var(--space-12);
        padding: var(--space-8);
        background: linear-gradient(135deg, rgba(255, 208, 0, 0.05) 0%, transparent 50%);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-xl);
        text-align: center;
        max-width: 700px;
      }
      
      .section-title {
        font-size: clamp(24px, 4vw, 32px);
        font-weight: 700;
        color: var(--rax-yellow);
        margin-bottom: var(--space-4);
      }
      
      .section-desc {
        font-size: var(--font-md);
        color: var(--text-secondary);
        line-height: 1.8;
      }
      
      .section-desc strong {
        color: var(--text-primary);
      }
      
      /* Responsive */
      @media (max-width: 768px) {
        .hero {
          padding: var(--space-4);
        }
      
        .logo {
          font-size: 64px;
        }
      
        .features {
          grid-template-columns: 1fr;
        }
      
        .cta-button {
          width: 100%;
          justify-content: center;
        }
      }
    `,
  ];

  @state()
  private loading = false;

  override render() {
    return html`
      <section class="hero">
        <div class="hero-content">
          <span class="logo">⚡</span>
          <h1 class="title">
            Your <span class="accent">AI Band</span>
          </h1>
          <p class="subtitle">
            Personal AI agents that actually work for you.
            One agent or many — watching work, home, projects.
            Each one private. All of them yours.
          </p>

          <div class="cta-section">
            <button class="cta-button" @click=${this.handleGetStarted} ?disabled=${this.loading}>
              <span class="icon">⚡</span>
              <span>${this.loading ? "Loading..." : "Start Your Band"}</span>
            </button>
            <button class="cta-secondary" @click=${this.handleSignIn}>
              Already have an account? Sign in
            </button>
          </div>

          <div class="features">
            <div class="feature">
              <div class="feature-icon">🔧</div>
              <div class="feature-title">Builds Its Own Tools</div>
              <div class="feature-desc">
                Not preset skills — your agent writes code, creates files, builds what it needs. Read, write, edit, execute. True autonomy.
              </div>
            </div>
            <div class="feature">
              <div class="feature-icon">🧠</div>
              <div class="feature-title">Real Memory</div>
              <div class="feature-desc">
                Persistent workspace. Your agent remembers conversations, learns preferences, evolves over time. Not stateless API calls.
              </div>
            </div>
            <div class="feature">
              <div class="feature-icon">🔒</div>
              <div class="feature-title">Radically Transparent</div>
              <div class="feature-desc">
                Every action logged. Every cost visible. Full audit trail of what your agent did and why — no black boxes.
              </div>
            </div>
            <div class="feature">
              <div class="feature-icon">💰</div>
              <div class="feature-title">You Control The Budget</div>
              <div class="feature-desc">
                Set limits per task, per day, per agent. Pause anytime. See exactly what you're paying for — pass-through costs, no markup.
              </div>
            </div>
            <div class="feature">
              <div class="feature-icon">📦</div>
              <div class="feature-title">Your Data, Your Rules</div>
              <div class="feature-desc">
                Export everything. Delete anytime. Your agent runs in your own isolated container — not shared infrastructure.
              </div>
            </div>
            <div class="feature">
              <div class="feature-icon">🎸</div>
              <div class="feature-title">Fleet-Native</div>
              <div class="feature-desc">
                Multiple agents from day one. Work, personal, family — each with its own context, memory, and permissions.
              </div>
            </div>
          </div>
          
          <div class="how-it-works">
            <h2 class="section-title">Not an API wrapper. A real agent.</h2>
            <p class="section-desc">
              Most "AI assistants" are stateless API calls with a chat UI. 
              Rax spins up a real environment for your agent — with a file system, 
              shell access, web browsing, and persistent memory. 
              It can write scripts, build tools, remember context across sessions.
              <strong>It's the difference between a chatbot and a coworker.</strong>
            </p>
          </div>
        </div>

        <footer class="hero-footer">
          <a href="https://rax.band" target="_blank">rax.band</a>
          <span>© 2025 Rax</span>
        </footer>
      </section>
    `;
  }

  private handleGetStarted() {
    this.loading = true;
    // Dispatch event for parent to handle (will trigger Clerk sign-up)
    this.dispatchEvent(new CustomEvent("get-started", { bubbles: true }));

    // Navigate to app (Clerk will handle auth)
    window.location.href = "/app";
  }

  private handleSignIn() {
    this.dispatchEvent(new CustomEvent("sign-in", { bubbles: true }));
    window.location.href = "/app";
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-landing": RaxLanding;
  }
}
